import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Modal from "./Modal";
import SelectInput from "./SelectInput";
import TeamMemberSelect from "./TeamMemberSelect";
import TeamBadge from "./TeamBadge";
import {
  teamFlowApis,
  teamListApis,
  teamMembersApis,
  moveTaskApis,
} from "../redux/team/teamActions";
import {
  selectFlowFromTeam,
  selectMembersOfTeam,
  selectMoveStatus,
  selectTeams,
} from "../redux/team/teamSelectors";
import { getHandoverLook } from "../utils/badgeColors";
import { isAdminRole } from "../utils/common";

/**
 * Hand a task to the next team — or send it back.
 *
 * The list of teams it offers comes from the same tbl_TeamFlow rows the server
 * validates against, so the dialog can only ever propose a move that will be
 * accepted. The server still re-checks: this is convenience, not the control.
 *
 * The exception is an admin, who is not held to the relay — for them the
 * dropdown lists every team, matching the route check that
 * sp_Queries_TeamHandover_Add waives for ADMIN and SUPER_ADMIN.
 *
 * Props:
 *   show, onClose
 *   task      : the row being moved (needs QueryCode, TeamCode, TeamName, TaskName)
 *   onMoved   : () => void  — called after a successful move, to refresh the grid
 */
const MoveTaskModal = ({ show, onClose, task, onMoved }) => {
  const dispatch = useDispatch();

  const isAdmin = useMemo(() => isAdminRole(), []);

  const fromTeamCode = task?.TeamCode ?? null;
  const flowOptions = useSelector(selectFlowFromTeam(fromTeamCode));
  const allTeams = useSelector(selectTeams);
  const moveStatus = useSelector(selectMoveStatus);

  const [toTeamCode, setToTeamCode] = useState("");
  const [toUserCode, setToUserCode] = useState("");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState({});

  const members = useSelector(selectMembersOfTeam(toTeamCode || null));

  /* Ask for the routes each time the dialog opens rather than once on mount:
     the same dialog is reused for every row in the grid, and each row may sit
     in a different team. */
  useEffect(() => {
    if (show && fromTeamCode) {
      dispatch(teamFlowApis({ fromTeamCode }));
    }
  }, [show, fromTeamCode, dispatch]);

  /* Admins choose from every team, so the dialog needs the full list. Asked
     for here rather than left to the host page: this modal opens from the
     Development and QA screens, and neither of those loads teams otherwise. */
  useEffect(() => {
    if (show && isAdmin) dispatch(teamListApis({}));
  }, [show, isAdmin, dispatch]);

  useEffect(() => {
    if (toTeamCode) dispatch(teamMembersApis({ teamCode: toTeamCode }));
  }, [toTeamCode, dispatch]);

  /* Start clean every time. Leaving the previous row's destination selected
     is how a task gets sent somewhere nobody intended. */
  useEffect(() => {
    if (show) {
      setToTeamCode("");
      setToUserCode("");
      setRemarks("");
      setErrors({});
    }
  }, [show, task?.QueryCode]);

  /* Everyone else is offered the configured routes only. An admin is offered
     every active team bar the one the task is already in, with the routed
     ones still labelled so "send back" keeps reading as a return rather than
     looking like any other destination. */
  const teamOptions = useMemo(() => {
    const labelFor = (code, name) => {
      const flow = (flowOptions || []).find((f) => f.ToTeamCode === code);
      return flow?.FlowType === "Return" ? `${name}  (send back)` : name;
    };

    if (isAdmin) {
      return (allTeams || [])
        .filter((t) => t.Status !== 0 && t.TeamCode !== fromTeamCode)
        .map((t) => ({
          value: t.TeamCode,
          label: labelFor(t.TeamCode, t.TeamName),
        }));
    }

    return (flowOptions || []).map((f) => ({
      value: f.ToTeamCode,
      label: labelFor(f.ToTeamCode, f.ToTeamName),
    }));
  }, [flowOptions, allTeams, isAdmin, fromTeamCode]);

  /* The assignee list is rendered by TeamMemberSelect rather than a text
     select: these names differ only after the first few letters, and the
     email is what tells them apart. */

  const selectedFlow = useMemo(
    () => (flowOptions || []).find((f) => f.ToTeamCode === toTeamCode),
    [flowOptions, toTeamCode],
  );

  /* The name for the destination badge. An admin can pick a team that has no
     route from here, and then there is no flow row to read it from — fall
     back to the team list so the badge still says where the task is going
     rather than reverting to "choose a team below". */
  const toTeamName = useMemo(() => {
    if (selectedFlow) return selectedFlow.ToTeamName;
    if (!toTeamCode) return null;
    return (
      (allTeams || []).find((t) => t.TeamCode === toTeamCode)?.TeamName || null
    );
  }, [selectedFlow, allTeams, toTeamCode]);

  const isReturn = selectedFlow?.FlowType === "Return";
  const look = getHandoverLook(isReturn ? "Return" : "Forward");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!toTeamCode) nextErrors.toTeamCode = "Select the team to move this to";

    /* Sending work back needs a reason. The whole point of a return is that
       something is still pending, and the next team cannot act on "returned"
       alone — so it is required here and optional on a forward. */
    if (isReturn && remarks.trim().length < 5) {
      nextErrors.remarks = "Say what is still pending — at least 5 characters";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const result = await dispatch(
      moveTaskApis({
        queryCode: task.QueryCode,
        toTeamCode,
        toUserCode: toUserCode || null,
        remarks: remarks.trim() || null,
      }),
    );

    if (moveTaskApis.fulfilled.match(result)) {
      toast.success(result.payload?.message || "Task moved.");
      onMoved?.();
      onClose?.();
    }
    // On failure the server's reason has already been shown as a toast.
  };

  if (!task) return null;

  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Move Task to Another Team"
      maxWidth="560px"
    >
      {/* The dialog's accent, picked up by every field inside it through
          formFields.css. One variable rather than a colour repeated on each
          control. */}
      <form
        onSubmit={handleSubmit}
        className="p-4 pt-3"
        style={{ "--sk-field-accent": "#4f46e5", "--sk-field-accent-soft": "rgba(79, 70, 229, 0.13)" }}
      >
        {/* What is being moved. A card rather than a label and a line of text:
            this is the fact the whole dialog is about, and the query number
            belongs with it — it is how people refer to a task out loud. */}
        <div
          className="d-flex align-items-start gap-3 mb-3"
          style={{
            background: "#f8fafc",
            border: "1px solid #eef1f6",
            borderRadius: "12px",
            padding: "12px 14px",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "#eef2ff",
              color: "#4f46e5",
            }}
          >
            <i className="bi bi-card-checklist" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div
              className="text-muted fw-bold text-uppercase"
              style={{ fontSize: "0.65rem", letterSpacing: "0.05em" }}
            >
              Task {task.QueryNo ? `#${task.QueryNo}` : ""}
            </div>
            <div
              className="fw-semibold"
              style={{ wordBreak: "break-word", color: "#1f2937", lineHeight: 1.35 }}
            >
              {task.TaskName || task.NatureofQuery || `#${task.QueryCode}`}
            </div>
          </div>
        </div>

        {/* Where it is going. The destination sits in a dashed slot until a
            team is picked, so the shape of the answer is visible before it is
            given and the row does not reflow when it arrives. */}
        <div
          className="d-flex align-items-center gap-2 mb-4 flex-wrap"
          style={{ minHeight: 34 }}
        >
          <TeamBadge name={task.TeamName} short={task.TeamShortName} />
          <i
            className={`bi ${look.icon}`}
            style={{ color: toTeamCode ? look.text : "#cbd5e1", fontSize: "1rem" }}
            aria-hidden="true"
          />
          {toTeamName ? (
            <TeamBadge name={toTeamName} />
          ) : (
            <span
              className="text-muted small"
              style={{
                border: "1px dashed #cbd5e1",
                borderRadius: "20px",
                padding: "3px 12px",
                fontSize: "0.75rem",
              }}
            >
              choose a team below
            </span>
          )}
          {isReturn && (
            <span
              className="ms-1 fw-semibold"
              style={{ color: look.text, fontSize: "0.75rem" }}
            >
              sending back
            </span>
          )}
        </div>

        {teamOptions.length === 0 ? (
          <div
            className="d-flex gap-2 mb-3"
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "10px",
              padding: "10px 12px",
              fontSize: "0.78rem",
              color: "#92400e",
            }}
          >
            <i
              className={`bi ${isAdmin ? "bi-hourglass-split" : "bi-exclamation-triangle-fill"} flex-shrink-0`}
            />
            {/* An admin's list is every team, so an empty one means the teams
                have not arrived yet — never that the route map is bare. */}
            <span>
              {isAdmin
                ? "Loading teams..."
                : fromTeamCode
                  ? "This team has no onward routes configured."
                  : "This task has no team yet, so it cannot be moved. Set its team on the Task Assignment screen first."}
            </span>
          </div>
        ) : (
          <>
            <SelectInput
              label="Move to Team"
              name="toTeamCode"
              value={toTeamCode}
              onChange={(e) => {
                setToTeamCode(e.target.value);
                // The previous team's person cannot be kept — they are not in
                // the new team, and the server would refuse the move.
                setToUserCode("");
              }}
              options={teamOptions}
              error={errors.toTeamCode}
              required
              placeholder="Select team"
            />

            <label className="sk-field__label">Assign to</label>
            <TeamMemberSelect
              members={members}
              value={toUserCode}
              onChange={setToUserCode}
              disabled={!toTeamCode}
              teamName={toTeamName}
            />

            {/* Says out loud when a team genuinely has nobody in it, rather
                than showing an empty list that looks like a loading failure. */}
            {toTeamCode && members.length === 0 && (
              <div
                className="d-flex gap-2 mb-3"
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "10px",
                  padding: "10px 12px",
                  fontSize: "0.78rem",
                  color: "#92400e",
                }}
              >
                <i className="bi bi-exclamation-triangle-fill flex-shrink-0" />
                <span>
                  <strong>{toTeamName}</strong> has no members yet. The task can
                  still go to the team, but nobody will hold it until someone is
                  added on the Users screen.
                </span>
              </div>
            )}
          </>
        )}

        {/* A return needs a reason, and saying so only after the user presses
            Move is too late — they have already written the dialog off as
            done. The rule is stated the moment the destination makes it
            apply. */}
        {isReturn && (
          <div
            className="d-flex gap-2 mb-3"
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              padding: "10px 12px",
              fontSize: "0.78rem",
              color: "#991b1b",
            }}
          >
            <i className="bi bi-arrow-return-left flex-shrink-0" />
            <span>
              This sends the task back to <strong>{toTeamName}</strong>. Say
              what is still pending so they can act on it.
            </span>
          </div>
        )}

        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <label className="sk-field__label">
              {isReturn ? "What is still pending?" : "Remarks"}
              {isReturn && <span className="sk-field__required">*</span>}
            </label>
            {/* Only where there is a minimum to meet — a counter on an
                optional box is noise. */}
            {isReturn && (
              <span
                className="text-muted"
                style={{ fontSize: "0.7rem", marginBottom: "6px" }}
              >
                {remarks.trim().length}/5 min
              </span>
            )}
          </div>
          <textarea
            className={`form-control modern-textarea${
              errors.remarks ? " is-invalid border-danger" : ""
            }`}
            rows="3"
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value);
              if (errors.remarks) setErrors((p) => ({ ...p, remarks: "" }));
            }}
            placeholder={
              isReturn
                ? "Explain what the other team still needs to do..."
                : "Anything the next team should know (optional)"
            }
          />
          {errors.remarks && (
            <div className="sk-field__error">
              <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
              <span>{errors.remarks}</span>
            </div>
          )}
        </div>

        {/* Footer. The note sits beside the buttons rather than above them —
            it is a standing fact about what the button does, not a step. */}
        <div
          className="d-flex align-items-center justify-content-between gap-3 flex-wrap pt-3"
          style={{ borderTop: "1px solid #eef1f6" }}
        >
          <div
            className="text-muted d-flex align-items-center gap-1"
            style={{ fontSize: "0.72rem" }}
          >
            <i className="bi bi-clock-history" aria-hidden="true" />
            Recorded in the task history with your name and the time.
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button type="button" className="btn btn-light border" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn d-inline-flex align-items-center gap-2"
              style={{
                backgroundColor: "#4f46e5",
                borderColor: "#4f46e5",
                color: "#fff",
                fontWeight: 600,
                minWidth: "132px",
                justifyContent: "center",
              }}
              disabled={moveStatus === "loading" || teamOptions.length === 0}
            >
              {moveStatus === "loading" ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                  Moving...
                </>
              ) : (
                <>
                  <i className={`bi ${look.icon}`} aria-hidden="true" />
                  {/* Naming the destination is the last check before it
                      happens — "Move Task" alone never told you where. */}
                  {toTeamName ? `Move to ${toTeamName}` : "Move Task"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default MoveTaskModal;
